import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BillingAPI, PlanInfo, SubscriptionStatus } from "../../services/billing.service";
import { formatPlanTotal } from "../../utils/billing";
import { PLAN_BASE_ANNUAL_PRICE_INR } from "../../constants";

const BASE_INCLUDED = [
  "Patient records & appointments",
  "Billing & invoices",
  "Basic reports",
  "Email & SMS notifications",
  "Offline sync",
];

const BASE_LOCKED = [
  "AI Scribe",
  "ABDM / ABHA sync",
  "Cloud backup (S3)",
  "Advanced analytics",
  "WhatsApp notifications",
];

interface Props {
  onClose: () => void;
}

const PlansModal: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([BillingAPI.getPlans(), BillingAPI.getSubscription()]).then(
      ([plansRes, subRes]) => {
        if (plansRes.status === "fulfilled") setPlans(plansRes.value ?? []);
        if (subRes.status === "fulfilled") setSubStatus(subRes.value);
        setLoading(false);
      }
    );
  }, []);

  const ACTIVE_STATUSES = ["active", "authenticated", "cancelled_scheduled"];
  const hasActiveSub = subStatus?.has_subscription && ACTIVE_STATUSES.includes(subStatus.status ?? "");

  // Pilot: Base annual only.
  const basePlan = plans.find((p) => p.tier === "base" && p.interval === "annual" && !p.is_trial);
  const isOnBase = hasActiveSub && subStatus?.plan_id === basePlan?.plan_id;
  const priceLabel = basePlan
    ? formatPlanTotal(basePlan)
    : `₹${PLAN_BASE_ANNUAL_PRICE_INR.toLocaleString("en-IN")}`;

  const handleUpgrade = () => {
    onClose();
    navigate("/settings", { state: { section: "finance" } });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[420px] overflow-hidden">

        <div className="px-8 pt-7 pb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight">Choose your plan</h2>
                <p className="text-sm text-slate-500 mt-0.5">Base annual — billed once a year</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 pb-6">
          {loading ? (
            <div className="h-96 rounded-2xl animate-pulse bg-slate-100" />
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col">
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg font-bold text-slate-900">Base</span>
                {isOnBase && (
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-500 text-white">Current Plan</span>
                )}
              </div>
              <p className="text-sm text-slate-400 mb-4">Cloud-based, all essentials</p>

              <div className="mb-4">
                <span className="text-3xl font-bold text-slate-900">{priceLabel}</span>
                <span className="text-sm text-slate-400 ml-1">/year</span>
              </div>

              <hr className="border-slate-100 mb-4" />

              <ul className="flex flex-col gap-2 flex-1">
                {BASE_INCLUDED.map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0 text-green-500" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
                {BASE_LOCKED.map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="text-sm shrink-0">🔒</span>
                    <span className="text-sm text-slate-400">{f}</span>
                  </li>
                ))}
              </ul>

              {isOnBase ? (
                <button disabled className="mt-6 w-full py-3 rounded-xl text-sm font-semibold text-slate-400 bg-slate-100 cursor-default">
                  You're on this plan
                </button>
              ) : (
                <button
                  onClick={handleUpgrade}
                  className="mt-6 w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)" }}
                >
                  Get Started →
                </button>
              )}
            </div>
          )}
        </div>

        <div className="px-8 pb-6 text-center">
          <span className="text-sm text-slate-400">Billed annually · exclusive of GST</span>
          <span className="text-sm text-slate-300 mx-2">→</span>
          <button
            onClick={handleUpgrade}
            className="text-sm text-teal-600 hover:underline font-medium"
          >
            Settings › Finance &amp; Billing
          </button>
        </div>

      </div>
    </div>
  );
};

export default PlansModal;
